import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { FileUpload } from '@mui/icons-material';
import { Checkbox, FormControl, FormControlLabel, InputLabel, Select, TextField, Typography } from '@mui/material';

import { UploadButton } from '@frontend/features/common/components/UploadButton';
import { WhiteHolder } from '@frontend/features/common/components/WhiteHolder';
import { useDynamicFields } from '@frontend/features/support-forms/components/DynamicFieldsContext';
import { RemovableField } from '@frontend/features/support-forms/components/RemovableField';

interface Props {
  title: string;
  editing?: boolean;
  onUpdateField?: (fieldName: string, value: unknown) => void;
}

export const FieldBlock = ({ title, editing, onUpdateField }: Props) => {
  const { t } = useTranslation();
  const { fields, updateFields } = useDynamicFields();
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});

  const formatDatetimeLocalFromISO = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value.slice(0, 16);
    }
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const formatDateOnly = (value?: string | null) => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    return value.slice(0, 10);
  };

  const formatTimestampLocal = (value?: string | null) => {
    if (!value) return '';
    if (value.includes('T')) {
      return value.slice(0, 16);
    }
    if (value.includes(' ')) {
      return value.replace(' ', 'T').slice(0, 16);
    }
    return value.slice(0, 16);
  };

  const handleChange = (name: string, value: unknown) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    onUpdateField && onUpdateField(name, value);
  };

  const handleRemoveField = (removeIndex: number) => {
    updateFields(fields.filter((_, index) => index !== removeIndex));
  };

  useEffect(() => {
    const result: Record<string, any> = {};

    fields && fields.forEach((field: any) => {
      result[field.name] = field.value !== undefined ? field.value : null;
    });

    setFormValues(result);

  }, [setFormValues, fields]);

  const renderedFields = useMemo(() => {
    return fields?.map((field, index) => {
      switch (field.fieldType) {
        case 'string':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <TextField
                fullWidth
                margin="normal"
                size="small"
                label={field.label}
                required={field.required}
                value={formValues[field.name] ?? ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
              />
            </RemovableField>
          );
        case 'longtext':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                margin="normal"
                size="small"
                label={field.label}
                required={field.required}
                value={formValues[field.name] ?? ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
              />
            </RemovableField>
          );
        case 'boolean':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues[field.name] ?? false}
                    onChange={(e) => handleChange(field.name, e.target.checked)}
                  />
                }
                label={field.label}
              />
            </RemovableField>
          );
        case 'select':

          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor={field.name} shrink>
                  {field.label}
                </InputLabel>
                <Select
                  displayEmpty
                  notched
                  native
                  size="small"
                  label={field.label}
                  inputProps={{ id: field.name }}
                  value={formValues[field.name] ?? ''}
                  onChange={(e) =>
                    handleChange(
                      field.name,
                      e.target.value === '' ? null : e.target.value,
                    )
                  }
                  required={field.required}
                >
                  <option key={field.label} value="">
                    {t('common.unassigned')}
                  </option>
                  {field.options?.map((option, idx) => (
                    <option key={idx} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </RemovableField>
          );
        case 'timestamptz':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                type="datetime-local"
                label={field.label}
                required={field.required}
                InputLabelProps={{ shrink: true }}
                value={formatDatetimeLocalFromISO(formValues[field.name])}
                onChange={(e) =>
                  handleChange(
                    field.name,
                    e.target.value ? new Date(e.target.value).toISOString() : null,
                  )
                }
              />
            </RemovableField>
          );
        case 'date':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                type="date"
                label={field.label}
                required={field.required}
                InputLabelProps={{ shrink: true }}
                value={formatDateOnly(formValues[field.name])}
                onChange={(e) => handleChange(field.name, e.target.value || null)}
              />
            </RemovableField>
          );
        case 'timestamp':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <TextField
                fullWidth
                size="small"
                margin="normal"
                type="datetime-local"
                label={field.label}
                required={field.required}
                InputLabelProps={{ shrink: true }}
                value={formatTimestampLocal(formValues[field.name])}
                onChange={(e) => handleChange(field.name, e.target.value || null)}
              />
            </RemovableField>
          );
        case 'file':
          return (
            <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
              <UploadButton
                component="label"
                variant="outlined"
                startIcon={<FileUpload />}
                name={field.name}
                required={field.required}
                onChange={(e) => handleChange(field.name, e.target.value)}
              >
                {field.label}
              </UploadButton>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formValues[field.name]?.split(/(\\|\/)/g).pop() || ''}
              </Typography>
            </RemovableField>
          );
        default:
          return null;
      }
    });
  }, [fields, formValues, handleChange, handleRemoveField, editing]);

  return (
    <WhiteHolder title={title}>
      {renderedFields}
    </WhiteHolder>
  );
};
